import type { ApiResponse, Curriculum, CurriculumListItem, CurriculumPlacement, CurriculumTocNode } from '../types';
import { getErrorMessage } from '../types';
import { authApi } from './auth';
import { API_BASE, fetchWithAdmin } from './fetchHelper';

/** Shared write helper — POST/DELETE with admin auth + 401 handling. */
async function writeJson<T>(method: 'POST' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const token = authApi.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) {
    authApi.logout();
    throw new Error('Session expired. Please login again.');
  }
  const data: ApiResponse<T> = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(getErrorMessage(data.error) || `API error: ${res.status} ${res.statusText}`);
  }
  return data.data as T;
}

export const curriculumApi = {
  // Curricula
  listCurricula: () =>
    fetchWithAdmin<{ curricula: CurriculumListItem[]; total: number }>('/curriculum'),
  getCurriculum: (id: string) =>
    fetchWithAdmin<{
      curriculum: Curriculum;
      placements: CurriculumPlacement[];
      questionCountByPlacement: Record<string, number>;
      questionCountsByModule: Record<string, number>;
      teacherQuestionCountByPlacement: Record<string, number>;
    }>(`/curriculum/${id}`),
  saveCurriculum: (c: Partial<Curriculum>) =>
    writeJson<Curriculum>('POST', '/curriculum', c),
  deleteCurriculum: (id: string) =>
    writeJson<{ deleted: boolean; placementsRemoved: number }>('DELETE', `/curriculum/${id}`),

  // Placements
  getPlacements: (id: string) =>
    fetchWithAdmin<{ placements: CurriculumPlacement[]; total: number }>(`/curriculum/${id}/placements`),
  savePlacement: (id: string, p: Partial<CurriculumPlacement>) =>
    writeJson<CurriculumPlacement>('POST', `/curriculum/${id}/placements`, p),
  deletePlacement: (id: string, placementId: string) =>
    writeJson<{ deleted: boolean; childrenRemoved: number }>('DELETE', `/curriculum/${id}/placements/${placementId}`),

  // Reconstructed table of contents (canonical tree + placements)
  getCurriculumToc: (id: string, subject: string) =>
    fetchWithAdmin<{ curriculumId: string; subject: string; nodes: CurriculumTocNode[] }>(
      `/curriculum/${id}/toc?subject=${encodeURIComponent(subject)}`,
    ),
};
